import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './BrandBuildStep.module.css';
import OnboardingStepper from '../shared/Stepper';

const BrandBuildStep = ({
  valueKey,
  topMainTitle = "Tell us about your business",
  topSubtitle = "This helps us understand how you sell so we can keep improving EcomBuddha for businesses like yours.",
  questionTitle,
  options,
  selectedValue,
  onNext,
  onGoBack,
  onSkip,
  onChange,
  error,
  autoAdvanceOnSelect = false,
  multiSelect = false,
  othersValue = '',
  onOthersChange,
  stepIndex = 1,
  totalSteps = 8,
  showSubmitButton = false,
  submitButtonLabel = 'Submit',
  nextQuestionTitle,
  nextOptions = [],
  direction = 'forward',
}) => {
  const othersInputRef = useRef(null);
  const normalizedOptions = useMemo(() => options || [], [options]);
  const initial = multiSelect ? (selectedValue || []) : (selectedValue || '');
  const [selected, setSelected] = useState(initial);

  useEffect(() => {
    if (!multiSelect && selectedValue && normalizedOptions.includes(selectedValue)) {
      setSelected(selectedValue);
    }
  }, [selectedValue, normalizedOptions, multiSelect]);


  const handleSelect = (option) => {
    let newSelected;
    if (multiSelect) {
      newSelected = selected.includes(option)
        ? selected.filter((o) => o !== option)
        : [...selected, option];
    } else {
      newSelected = option;
    }
    setSelected(newSelected);
    if (onChange) onChange({ [valueKey]: newSelected });
    if (!multiSelect && autoAdvanceOnSelect && onNext) {
      setTimeout(() => onNext({ [valueKey]: newSelected }), 300);
    }
  };

  return (
    <div className={styles.box}>
      {onSkip && (
        <button
          type="button"
          className={styles.skipLink}
          onClick={onSkip}
          aria-label="Skip for now"
        >
          Skip for now
        </button>
      )}

      <div className={styles.layout}>
        {onGoBack && (
          <button type="button" className={styles.goBackBtn} onClick={onGoBack}>
            <svg width="9" height="15" viewBox="0 0 9 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.28809 0.358398L0.348633 7.5L7.28809 14.6426L8.65234 13.2373L3.25781 7.67383L3.08887 7.5L3.25781 7.32617L8.65234 1.76367L7.28809 0.358398Z" fill="black" fill-opacity="0.6" stroke="white" stroke-width="0.5"/>
            </svg>
            Go back
          </button>
        )}
        <div className={styles.topSection}>
          <div className={styles.mainTitle}>{topMainTitle}</div>
          <div className={styles.subtitle}>
            {topSubtitle}
          </div>

          <div className={styles.progressBar}>
            <div className={styles.progressBarBg} />
            <div className={styles.progressBarFill} style={{ width: `${((stepIndex + 1) / totalSteps) * 176}px` }} />
          </div>
        </div>

        <div className={styles.questionsTrackOuter}>
          <div key={questionTitle} className={`${styles.questionsTrack} ${direction === 'back' ? styles.questionsTrackBack : ''}`}>
          <div className={styles.questionSection}>
            <div className={styles.questionTitle}>{questionTitle}</div>

            <div className={styles.chipsRow} role="group" aria-label="Primary brand build method">
              {normalizedOptions.map((opt) => {
                const isSelected = multiSelect ? selected.includes(opt) : selected === opt;
                const isOthers = opt === 'Others' || opt === 'Other';
                const othersSelected = isOthers && (multiSelect ? selected.includes(opt) : selected === opt);
                return (
                  <React.Fragment key={opt}>
                    <button
                      type="button"
                      className={`${styles.chip} ${isSelected ? styles.chipSelected : ''}`}
                      onClick={() => handleSelect(opt)}
                      aria-pressed={isSelected}
                    >
                      <span className={`${styles.chipText} ${isSelected ? styles.chipTextSelected : ''}`}>
                        {opt}
                      </span>
                    </button>
                    {othersSelected && (
                      <input
                        ref={othersInputRef}
                        type="text"
                        className={styles.othersInput}
                        placeholder="Please specify..."
                        defaultValue={othersValue}
                        onChange={(e) => onOthersChange && onOthersChange(e.target.value)}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {error && (
              <div className={styles.errorText}>{error}</div>
            )}

            {multiSelect && (
              <div className={styles.multiSelectActions}>
                <div className={styles.multiSelectHint}>Select all that apply</div>
                {showSubmitButton && (
                  <button
                    type="button"
                    className={styles.nextBtn}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const currentOthers = othersInputRef.current ? othersInputRef.current.value : othersValue;
                      const value = multiSelect
                        ? selected.map((o) => ((o === 'Others' || o === 'Other') && currentOthers ? currentOthers : o))
                        : ((selected === 'Others' || selected === 'Other') && currentOthers ? currentOthers : selected);
                      onNext && onNext({ [valueKey]: value });
                    }}
                    disabled={
                      (multiSelect ? selected.length === 0 : !selected) ||
                      ((multiSelect ? selected.some((o) => o === 'Others' || o === 'Other') : (selected === 'Others' || selected === 'Other')) && !othersValue.trim())
                    }
                  >
                    {submitButtonLabel}
                  </button>
                )}
                {!showSubmitButton && (
                  <button
                    type="button"
                    className={styles.nextBtn}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const currentOthers = othersInputRef.current ? othersInputRef.current.value : othersValue;
                      const value = selected.map((o) => ((o === 'Others' || o === 'Other') && currentOthers ? currentOthers : o));
                      onNext && onNext({ [valueKey]: value });
                    }}
                    disabled={
                      selected.length === 0 ||
                      (selected.some((o) => o === 'Others' || o === 'Other') && !othersValue.trim())
                    }
                  >
                    Next
                  </button>
                )}
              </div>
            )}

          </div>

          <div className={styles.questionPeek} aria-hidden="true" style={{ visibility: nextQuestionTitle ? 'visible' : 'hidden' }}>
              <div className={styles.questionTitle}>{nextQuestionTitle}</div>
              <div className={`${styles.chipsRow} ${styles.chipsRowNoWrap}`}>
                {nextOptions.map((opt) => (
                  <span key={opt} className={styles.chip}>
                    <span className={styles.chipText}>{opt}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandBuildStep;

